import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Grid, Typography, useMediaQuery, Container, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useSnackbar } from 'notistack';
import { retrieveConsultancyDetails } from './../../../actions';
import * as campaignActions from './../../../actions/campaignActions';
import * as talentAlertApi from './../../../api/talentAlertApi';
import { PageTitle } from './../../../components/LayoutComponents/PageTitle';
import SignInModal from './../UnAuthTalentAlertContactCandidateProfile/SignInModal';
import SingleCandidate from './../TalentAlertContactDashboard/SingleCandidate';
import { SHORTLIST, FIRST_INTERVIEW, FURTHER_INTERVIEW, OFFER_MADE } from './../../../constants/candidateStatusIds';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    overflow: 'hidden'
  },
  headerSection: {
    position: 'relative',
    width: '100%',
    minHeight: '55vh',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      minHeight: '560px'
    }
  },
  headerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: 1
  },
  headerContent: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
    maxWidth: '1200px',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  logo: {
    maxWidth: '270px',
    width: '100%',
    height: 'auto',
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      maxWidth: '200px',
      marginBottom: theme.spacing(3)
    }
  },
  videoContainer: {
    width: '100%',
    maxWidth: '600px',
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '90%'
    }
  },
  video: {
    width: '100%',
    height: 'auto',
    maxHeight: '400px',
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[4]
  },
  introSection: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(6, 2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(4, 2)
    }
  },
  introContainer: {
    maxWidth: '1200px',
    margin: '0 auto'
  },
  introHeader: {
    fontSize: '2rem',
    marginBottom: theme.spacing(3),
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
      marginBottom: theme.spacing(2)
    }
  },
  introText: {
    fontSize: '1.1rem',
    lineHeight: 1.6,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem'
    }
  },
  candidatesSection: {
    padding: theme.spacing(4, 2),
    maxWidth: '1200px',
    margin: '0 auto'
  },
  candidateWrapper: {
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    transition: 'all 0.3s ease',
    '&:hover': {
      cursor: 'pointer',
      boxShadow: theme.shadows[2],
      transform: 'translateY(-2px)'
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center'
    }
  },
  candidateName: {
    fontSize: '1.5rem',
    fontWeight: 600,
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.3rem',
      marginBottom: theme.spacing(1)
    }
  },
  candidateLocation: {
    fontSize: '1.3rem',
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.1rem'
    }
  }
}));

const UnAuthTalentAlertContactDashboard = ({ loggedIn, campaignId, isPreview, cancelPreview }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [t] = useTranslation(['newTranslations']);
  const isMobile = useMediaQuery('(max-width:767px)');
  const contactCampaign = useSelector((state) => state.campaign);
  const { consultancyDetails } = useSelector(state => state.consultancy);
  const [signInModalOpen, setSignInModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [consultancyId, setConsultancyId] = useState(0);
  const [campaignTitle, setCampaignTitle] = useState('');
  const campaignCandidates = contactCampaign.campaignCandidates;

  if (contactCampaign?.campaignTitle && !campaignTitle) {
    setCampaignTitle(contactCampaign.campaignTitle);
  }
  
  useEffect(() => {
    const fetchDetails = () => {
      retrieveConsultancyDetails(dispatch, { host: window.location.hostname, route: "" });
    };
    if (!consultancyDetails) {
      fetchDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    const fetchConsultancyIdAndData = async () => {
      const consultancyId = await getConsultancyId();
      
      if (loggedIn) {
        history.push(`/dashboard/talent-alerts/${campaignId}`);
        return;
      }
  
      setIsLoading(true);
      if (consultancyId !== 0 && consultancyId !== null) {
        try {
          const response = await talentAlertApi.getContactHiddenTalentAlertById(campaignId, consultancyId);
          dispatch({
            type: campaignActions.UPDATE_CAMPAIGN,
            campaign: response.data,
          });
        } catch (error) {
          enqueueSnackbar(t('newTranslations:failedToGetTalentAlert'), { variant: 'error' });
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };
  
    fetchConsultancyIdAndData();
  
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, campaignId]);

  const getConsultancyId = async () => {
    try {
      const response = await talentAlertApi.getConsultancyId(window.location.hostname);
      setConsultancyId(response.data);
      return response.data;
    } catch (err) {
      console.log(err);
      return null;
    }
  };

  const toggleSignInModal = () => {
    setSignInModalOpen((prevState) => !prevState);
  };

  const campaignCandidatesFilter = (candidate) => {
    switch (candidate.status) {
      case SHORTLIST:
      case FIRST_INTERVIEW:
      case FURTHER_INTERVIEW:
      case OFFER_MADE:
        return true;
      default:
        return false;
    }
  };

  const getHeaderLogoSrc = () => {
    if (contactCampaign?.campaignLogoImg) {
      return contactCampaign.campaignLogoImg;
    }
    return '';
  }
  
  return (
    <>
      <Helmet>
        <title>{t("newTranslations:talentAlert")}</title>
        <meta property='og:image' content={contactCampaign?.headerImg} />
        <meta property='og:image:type' content='image/png' />
        <meta property='og:image:width' content='1200' />
        <meta property='og:image:height' content='2292' />
      </Helmet>
      
      <div className={classes.root} loading={isLoading} removeGutters>
        {isPreview && (
          <PageTitle
            pageTitle={ `${campaignTitle} - ${t("newTranslations:recruiterPreviewTalentAlertAsContactPreviewHeaderAdditionalLabel")}` }
            btnLabel={ t("newTranslations:recruiterPreviewTalentAlertAsContactBtnCancelPreview") }
            btnFunc={ cancelPreview }
            btnVariant="outlined" />
        )}
        
        {/* Header Section with Background Image */}
        <Box 
          className={classes.headerSection}
          style={{ backgroundImage: `url(${contactCampaign?.headerImg})` }}
        >
          <div className={classes.headerOverlay} />
          <Container className={classes.headerContent}>
            {getHeaderLogoSrc() && (
              <img 
                src={getHeaderLogoSrc()} 
                alt="Campaign Logo" 
                className={classes.logo} 
              />
            )}
            
            {contactCampaign.campaignVideo && (
              <div className={classes.videoContainer}>
                <video 
                  className={classes.video}
                  src={contactCampaign.campaignVideo} 
                  controls 
                  allowFullScreen
                  preload="metadata"
                />
              </div>
            )}
          </Container>
        </Box>
        
        {/* Introduction Section */}
        <Box className={classes.introSection}>
          <Container className={classes.introContainer}>
            <Grid container justifyContent="center" alignItems="center">
              <Grid item xs={12} md={8} lg={6} style={{ margin: '0 auto' }}>
                <Typography className={classes.introHeader} align="center">
                  {contactCampaign.introduction?.greetingCaption}
                </Typography>
                
                <Typography className={classes.introText} align="center">
                  {contactCampaign.introduction?.introductionText}
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </Box>
        
        {/* Candidates Section */}
        <Box className={classes.candidatesSection}>
          {campaignCandidates
            .filter(campaignCandidatesFilter)
            .map((candidate) => (
              <SingleCandidate
                candidateData={candidate}
                campaignId={campaignId}
                consultancyId={consultancyId}
                key={candidate?.id}
                loggedIn={loggedIn} />
            ))
          }
        </Box>
        
        <SignInModal
          open={signInModalOpen}
          handleClose={toggleSignInModal}
          campaignId={campaignId} />
      </div>
    </>
  );
};

export default UnAuthTalentAlertContactDashboard;